<script setup>
const props = defineProps({
    button: {
        type: Object,
        default: null,
    },
    label: { type: String, default: '' },
    url: { type: String, default: '' },
    title: { type: String, default: '' },
    target: { type: Boolean, default: false },
    variant: {
        type: String,
        default: 'magenta',
        validator: (v) => ['magenta', 'pink', 'white'].includes(v),
    },
    // Only meaningful for the buttonless (no-url) form — a link can't be
    // disabled. Additive and off by default, so existing usage is unchanged.
    disabled: { type: Boolean, default: false },
});

defineEmits(['click']);

const resolvedLabel = computed(() => props.button?.label ?? props.label);
const resolvedUrl = computed(() => props.button?.url ?? props.url);
const resolvedTitle = computed(() => props.button?.title ?? props.title);
const resolvedTarget = computed(() => props.button?.target ?? props.target);

// Resting and hover halves are kept apart so a disabled button can take the
// resting colours without the hover rules — outspecifying them with
// `disabled:hover:` stacks is fragile, and a disabled button that still lights
// up on hover reads as clickable.
const VARIANTS = {
    pink: {
        base: 'border-[var(--pink)] text-[var(--pink)]',
        hover: 'hover:bg-white hover:text-[var(--magenta)] hover:border-white',
    },
    // Solid white pill with magenta text (e.g. over the countdown gradient);
    // hovers to a solid orange pill with white text.
    white: {
        base: 'bg-white border-white text-[var(--magenta)]',
        hover: 'hover:bg-[var(--orange)] hover:border-[var(--orange)] hover:text-white',
    },
    magenta: {
        base: 'border-[var(--magenta)] text-[var(--magenta)]',
        hover: 'hover:bg-[var(--magenta)] hover:text-white',
    },
};

const disabledClass = 'opacity-40 cursor-not-allowed';

const baseVariantClass = computed(() => (VARIANTS[props.variant] ?? VARIANTS.magenta).base);
const variantClass = computed(() => {
    const v = VARIANTS[props.variant] ?? VARIANTS.magenta;
    return `${v.base} ${v.hover}`;
});

const sharedClass = 'inline-flex items-center justify-center gap-[10px] shrink-0 '
    + 'h-[42px] px-[20px] rounded-[50px] border whitespace-nowrap '
    + 'no-underline font-hubspot-serif '
    + 'text-[17px] leading-[1.4] tracking-[0.34px] '
    + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current '
    + 'transition-colors duration-200 cursor-pointer';
</script>

<template>
    <MessLink v-if="resolvedUrl"
        :href="resolvedUrl"
        :title="resolvedTitle"
        :label="resolvedLabel"
        :target="resolvedTarget"
        :class="[sharedClass, variantClass]"
        @click="$emit('click', $event)">
        <slot>{{ resolvedLabel }}</slot>
    </MessLink>
    <button v-else
        type="button"
        :title="resolvedTitle"
        :aria-label="resolvedLabel"
        :disabled="disabled"
        :class="[sharedClass, disabled ? baseVariantClass : variantClass, 'bg-transparent', disabled && disabledClass]"
        @click="$emit('click', $event)">
        <slot>{{ resolvedLabel }}</slot>
    </button>
</template>
